import { useEffect, useState } from "react";
import { leadsApi, clientsApi, jobsApi, ApiError } from "@/api";
import type { Lead, LeadCreateInput, LeadCreateStage, LeadSource, JobClientPickerItem } from "@/types";
import { Button, TextField, TextAreaField, SelectField } from "@/components/ui";
import { stageLabel } from "@/lib/leads";
import { lookupPostcodeAddresses, postcodeLookupAvailable, type PostcodeAddress } from "@/lib/postcode";
import { FormDrawer } from "../jobs/FormDrawer";

/**
 * Manual "Add lead" form (direct-contact route — past clients, referrals). A
 * right-side drawer (the shared FormDrawer shell) to match the New job / New
 * client create forms. Posts to api 214 (`leadsApi.createLead`), which always
 * inserts (duplicates are intentional) and maps `lead_source` → source/medium
 * server-side. On success the caller adds the card and opens the lead detail
 * drawer so staff can carry on.
 *
 * Source-led: lead source + its follow-up sit at the top. "Referred by" only
 * shows for the referral sources; "Past client" reveals a client search that
 * back-fills the contact details (and the site address, when the client has a
 * single job — ambiguous when they have several, so we leave it blank).
 */

const SOURCES: { value: LeadSource; label: string }[] = [
  { value: "past_client", label: "Past client" },
  { value: "client_referral", label: "Client referral" },
  { value: "builder_referral", label: "Builder referral" },
  { value: "direct", label: "Direct (phone/email)" },
  { value: "other", label: "Other" },
];

const CREATE_STAGES: LeadCreateStage[] = ["new", "to_schedule", "qualified"];

export function AddLeadDrawer({
  onCreated,
  onClose,
}: {
  onCreated: (lead: Lead) => void;
  onClose: () => void;
}) {
  const [source, setSource] = useState<LeadSource | "">("");
  const [referredBy, setReferredBy] = useState("");
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [postcode, setPostcode] = useState("");
  const [address, setAddress] = useState("");
  const [line2, setLine2] = useState("");
  const [line3, setLine3] = useState("");
  const [city, setCity] = useState("");
  const [county, setCounty] = useState("");
  // Captured silently from a Find-address pick; passed through on save, no field shown.
  const [district, setDistrict] = useState(""); // LPA (Ideal Postcodes `district`)
  const [uprn, setUprn] = useState("");
  const [lat, setLat] = useState(0);
  const [lng, setLng] = useState(0);
  // Postcode → address lookup (Ideal Postcodes), mirroring LeadDrawer's SiteLookup.
  const [lookupBusy, setLookupBusy] = useState(false);
  const [lookupHits, setLookupHits] = useState<PostcodeAddress[]>([]);
  const [description, setDescription] = useState("");
  // Optional here — it gates Create quote in the lead drawer, not lead creation.
  const [quoteDescription, setQuoteDescription] = useState("");
  const [stage, setStage] = useState<LeadCreateStage>("new");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | undefined>();

  // Past-client lookup (only shown when source === "past_client").
  const [clientQuery, setClientQuery] = useState("");
  const [clientResults, setClientResults] = useState<JobClientPickerItem[]>([]);
  const [picked, setPicked] = useState<JobClientPickerItem | null>(null);
  const [searching, setSearching] = useState(false);
  const [pickNote, setPickNote] = useState<string | undefined>();

  const showReferredBy = source === "client_referral" || source === "builder_referral";
  const isPastClient = source === "past_client";

  // Mirror the server's validation. We validate on click (rather than disabling the button)
  // so an incomplete form gives a clear reason instead of a silent dead button.
  const hasName = !!(first.trim() || last.trim());
  const hasContact = !!(email.trim() || phone.trim());

  // Debounced type-ahead, reusing the lean Jobs client picker. Skip once a client
  // is picked (the input is then replaced by the chosen client; Change re-opens it).
  useEffect(() => {
    if (!isPastClient || picked) return;
    const term = clientQuery.trim();
    if (!term) {
      setClientResults([]);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(() => {
      jobsApi
        .listJobClients(term)
        .then((rows) => {
          if (!cancelled) setClientResults(rows ?? []);
        })
        .catch(() => {
          if (!cancelled) setClientResults([]);
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [clientQuery, isPastClient, picked]);

  function handleSourceChange(next: LeadSource | "") {
    setSource(next);
    setError(undefined);
    // Drop a stale referrer when the new source doesn't take one.
    if (next !== "client_referral" && next !== "builder_referral") setReferredBy("");
    // Collapse the picker when leaving past-client (keep any back-filled details).
    if (next !== "past_client") {
      setClientQuery("");
      setClientResults([]);
    }
  }

  /** Back-fill the form from a chosen past client's saved record. Site address
   *  comes from their job only when there's exactly one — otherwise it's
   *  ambiguous, so we leave it for staff to fill. */
  async function pickClient(item: JobClientPickerItem) {
    setPicked(item);
    setClientResults([]);
    setPickNote(undefined);
    try {
      const { client, jobs } = await clientsApi.getClientDetail(item.id);
      setFirst(client.contact_first_name || "");
      setLast(client.contact_last_name || "");
      setCompany(client.company_name || "");
      setEmail(client.email || "");
      setPhone(client.phone || "");
      // `jobs` already excludes merged-away jobs server-side. Populate the site
      // only when there's exactly one job — with several it's ambiguous, so
      // leave it for staff to fill. (is_lead here flags the client's role on the
      // job, not a lead-stage stub, so it isn't a filter.)
      // A back-fill replaces the site, so drop any structured extras from an
      // earlier Find-address pick (line 1 + postcode come from the single job).
      clearAddressExtras();
      if (jobs.length === 1) {
        setAddress(jobs[0].address || "");
        setPostcode(jobs[0].postcode || "");
      } else {
        setAddress("");
        setPostcode("");
        if (jobs.length > 1) {
          setPickNote(`This client has ${jobs.length} jobs — add the site address for this lead.`);
        }
      }
    } catch {
      setError("Couldn't load that client's saved details — enter them below.");
    }
  }

  function changeClient() {
    setPicked(null);
    setClientQuery("");
    setClientResults([]);
    setPickNote(undefined);
  }

  /** Reset the structured address fields captured from a Find-address pick. */
  function clearAddressExtras() {
    setLine2("");
    setLine3("");
    setCity("");
    setCounty("");
    setDistrict("");
    setUprn("");
    setLat(0);
    setLng(0);
    setLookupHits([]);
  }

  /** Look up the typed postcode → list of addresses for the user to pick from. */
  async function findAddress() {
    const pc = postcode.trim();
    if (!pc || lookupBusy) return;
    setLookupBusy(true);
    setLookupHits([]);
    setError(undefined);
    try {
      const hits = await lookupPostcodeAddresses(pc);
      if (hits.length === 0) {
        setError("No addresses found for that postcode — check it and try again.");
        return;
      }
      setLookupHits(hits);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Postcode lookup failed.");
    } finally {
      setLookupBusy(false);
    }
  }

  /** Fill the address fields (and silently capture uprn/lat/lng) from a picked hit. */
  function applyAddress(hit: PostcodeAddress) {
    setAddress(hit.line1);
    setLine2(hit.line2);
    setLine3(hit.line3);
    setCity(hit.postTown);
    setCounty(hit.county);
    setDistrict(hit.council);
    setPostcode(hit.postcode);
    setUprn(hit.uprn);
    setLat(hit.latitude);
    setLng(hit.longitude);
  }

  async function submit() {
    if (saving) return;
    if (!hasName) {
      setError("Add a first or last name.");
      return;
    }
    if (!hasContact) {
      setError("Add an email or a phone number.");
      return;
    }
    setSaving(true);
    setError(undefined);
    const input: LeadCreateInput = {
      contact_first_name: first.trim(),
      contact_last_name: last.trim(),
      company_name: company.trim(),
      email: email.trim(),
      phone: phone.trim(),
      project_description: description.trim(),
      quote_description: quoteDescription.trim(),
      site_address_line_1: address.trim(),
      site_address_line_2: line2.trim(),
      site_address_line_3: line3.trim(),
      site_city: city.trim(),
      site_county: county.trim(),
      site_district: district,
      site_postcode: postcode.trim(),
      uprn,
      lat,
      lng,
      referred_by: showReferredBy ? referredBy.trim() : "",
      stage,
    };
    if (source) input.lead_source = source;
    try {
      const lead = await leadsApi.createLead(input);
      onCreated(lead);
    } catch (err) {
      // Keep the drawer open and the form intact; just surface the message.
      setError(err instanceof ApiError ? err.message : "Could not add the lead.");
      setSaving(false);
    }
  }

  return (
    <FormDrawer
      title="Add lead"
      busy={saving}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={submit} loading={saving}>Add lead</Button>
        </>
      }
    >
      {error ? <p className="leads__error" role="alert" onClick={() => setError(undefined)}>{error}</p> : null}

      <section className="job-drawer__section">
        <h3>Source</h3>
        <SelectField label="Lead source" value={source} onChange={(e) => handleSourceChange(e.target.value as LeadSource | "")}>
          <option value="">Select a source…</option>
          {SOURCES.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </SelectField>

        {showReferredBy ? (
          <TextField label="Referred by" value={referredBy} onChange={(e) => setReferredBy(e.target.value)} placeholder="Who sent them?" />
        ) : null}

        {isPastClient ? (
          <div className="job-client-picker">
            {picked ? (
              <div className="job-client-picked">
                <div>
                  <strong>{picked.display_name}</strong>
                  {picked.email ? <span className="job-drawer__muted"> · {picked.email}</span> : null}
                </div>
                <Button variant="ghost" size="sm" onClick={changeClient}>Change</Button>
              </div>
            ) : (
              <>
                <input
                  className="leads__search"
                  type="search"
                  placeholder="Search past clients by name or email…"
                  value={clientQuery}
                  onChange={(e) => setClientQuery(e.target.value)}
                />
                {searching ? <p className="job-drawer__muted">Searching…</p> : null}
                {!searching && clientQuery.trim() && clientResults.length === 0 ? (
                  <p className="job-drawer__muted">No clients found — enter their details below.</p>
                ) : null}
                {clientResults.length > 0 ? (
                  <ul className="job-client-results">
                    {clientResults.map((c) => (
                      <li key={c.id}>
                        <button type="button" className="job-client-result" onClick={() => pickClient(c)}>
                          <span className="job-client-result__name">{c.display_name}</span>
                          {c.email ? <span className="job-client-result__email">{c.email}</span> : null}
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </>
            )}
            {pickNote ? <p className="job-drawer__muted">{pickNote}</p> : null}
          </div>
        ) : null}
      </section>

      <section className="job-drawer__section">
        <h3>Contact</h3>
        <div className="lead-drawer__two">
          <TextField label="First name" value={first} onChange={(e) => setFirst(e.target.value)} />
          <TextField label="Last name" value={last} onChange={(e) => setLast(e.target.value)} />
        </div>
        <TextField label="Company (optional)" value={company} onChange={(e) => setCompany(e.target.value)} />
        <div className="lead-drawer__two">
          <TextField label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <TextField label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
        </div>
        {!hasContact ? <p className="lead-drawer__muted">Enter an email or a phone number.</p> : null}
      </section>

      <section className="job-drawer__section">
        <h3>Project</h3>
        <div className="lead-drawer__lookup">
          <TextField
            label="Site postcode"
            value={postcode}
            onChange={(e) => setPostcode(e.target.value.toUpperCase())}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                findAddress();
              }
            }}
          />
          {postcodeLookupAvailable ? (
            <div>
              <Button
                variant="secondary"
                size="sm"
                onClick={findAddress}
                loading={lookupBusy}
                disabled={!postcode.trim()}
              >
                Find address
              </Button>
            </div>
          ) : null}
          {lookupHits.length > 0 ? (
            <SelectField
              label={`Select an address (${lookupHits.length} found)`}
              value=""
              onChange={(e) => {
                if (e.target.value !== "") applyAddress(lookupHits[Number(e.target.value)]);
              }}
            >
              <option value="">Choose…</option>
              {lookupHits.map((h, i) => (
                <option key={h.uprn || i} value={i}>{h.label}</option>
              ))}
            </SelectField>
          ) : null}
        </div>
        <TextField label="Site address line 1" value={address} onChange={(e) => setAddress(e.target.value)} />
        <TextField label="Site address line 2" value={line2} onChange={(e) => setLine2(e.target.value)} />
        <TextField label="Site address line 3" value={line3} onChange={(e) => setLine3(e.target.value)} />
        <div className="lead-drawer__two">
          <TextField label="Town / City" value={city} onChange={(e) => setCity(e.target.value)} />
          <TextField label="County" value={county} onChange={(e) => setCounty(e.target.value)} />
        </div>
        <TextAreaField
          label="Project description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="e.g. rear + side-return extension"
        />
        <TextField
          label="Description for quote"
          value={quoteDescription}
          onChange={(e) => setQuoteDescription(e.target.value)}
          placeholder="e.g. a single-storey rear extension"
          hint="Optional now; needed before a quote can be created. Completes the sentence on the quote saying what it covers."
        />
        <SelectField label="Stage" value={stage} onChange={(e) => setStage(e.target.value as LeadCreateStage)}>
          {CREATE_STAGES.map((s) => (
            <option key={s} value={s}>{stageLabel(s)}</option>
          ))}
        </SelectField>
      </section>
    </FormDrawer>
  );
}
